import { REPORT_BLOCKS, type ReportBlock, type BlockType } from "@/lib/reports/block-types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface BlockSettingsProps {
  block: ReportBlock | null;
  onUpdateConfig: (blockId: string, config: Record<string, unknown>) => void;
}

export function BlockSettings({ block, onUpdateConfig }: BlockSettingsProps) {
  if (!block) {
    return (
      <div className="w-[320px] border-l p-4 shrink-0 overflow-y-auto">
        <p className="text-sm text-muted-foreground text-center mt-8">
          Select a block to configure its settings.
        </p>
      </div>
    );
  }

  const definition = REPORT_BLOCKS[block.type as BlockType];
  if (!definition) return null;

  const Icon = definition.icon;
  const config = block.config;

  const updateConfig = (key: string, value: unknown) => {
    onUpdateConfig(block.id, { ...config, [key]: value });
  };

  return (
    <div className="w-[320px] border-l p-4 shrink-0 overflow-y-auto space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold text-sm">{definition.name}</h3>
      </div>

      {/* Block-specific settings */}
      {block.type === "executive_summary" && (
        <>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={(config.style as string) || "ai_generated"}
              onValueChange={(v) => updateConfig("style", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ai_generated">AI Generated</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {config.style === "manual" && (
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={(config.content as string) || ""}
                onChange={(e) => updateConfig("content", e.target.value)}
                rows={6}
                placeholder="Write your executive summary..."
              />
            </div>
          )}
        </>
      )}

      {block.type === "holdings_table" && (
        <>
          <div className="space-y-2">
            <Label>Sort By</Label>
            <Select
              value={(config.sortBy as string) || "market_value"}
              onValueChange={(v) => updateConfig("sortBy", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="market_value">Market Value</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="gain_loss">Gain/Loss</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Show Cost Basis</Label>
            <Switch
              checked={(config.showCost as boolean) ?? true}
              onCheckedChange={(v) => updateConfig("showCost", v)}
            />
          </div>
        </>
      )}

      {block.type === "asset_allocation" && (
        <>
          <div className="space-y-2">
            <Label>Chart Type</Label>
            <Select
              value={(config.chartType as string) || "pie"}
              onValueChange={(v) => updateConfig("chartType", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pie">Pie Chart</SelectItem>
                <SelectItem value="bar">Bar Chart</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Show Table</Label>
            <Switch
              checked={(config.showTable as boolean) ?? true}
              onCheckedChange={(v) => updateConfig("showTable", v)}
            />
          </div>
        </>
      )}

      {block.type === "top_holdings" && (
        <div className="space-y-2">
          <Label>Number of Holdings</Label>
          <Input
            type="number"
            min={5}
            max={50}
            value={(config.topN as number) || 10}
            onChange={(e) => updateConfig("topN", parseInt(e.target.value) || 10)}
          />
        </div>
      )}

      {block.type === "custom_text" && (
        <div className="space-y-2">
          <Label>Content</Label>
          <Textarea
            value={(config.content as string) || ""}
            onChange={(e) => updateConfig("content", e.target.value)}
            rows={8}
            placeholder="Enter your text..."
          />
        </div>
      )}

      {block.type === "section_header" && (
        <>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={(config.title as string) || ""}
              onChange={(e) => updateConfig("title", e.target.value)}
              placeholder="Section title"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Show Divider</Label>
            <Switch
              checked={(config.showDivider as boolean) ?? true}
              onCheckedChange={(v) => updateConfig("showDivider", v)}
            />
          </div>
        </>
      )}

      {block.type === "performance_attribution" && (
        <>
          <div className="flex items-center justify-between">
            <Label>Show Contributors</Label>
            <Switch
              checked={(config.showContributors as boolean) ?? true}
              onCheckedChange={(v) => updateConfig("showContributors", v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Top N</Label>
            <Input
              type="number"
              min={3}
              max={20}
              value={(config.topN as number) || 5}
              onChange={(e) => updateConfig("topN", parseInt(e.target.value) || 5)}
            />
          </div>
        </>
      )}

      {block.type === "transaction_log" && (
        <div className="space-y-2">
          <Label>Max Transactions</Label>
          <Input
            type="number"
            min={10}
            max={500}
            value={(config.limit as number) || 50}
            onChange={(e) => updateConfig("limit", parseInt(e.target.value) || 50)}
          />
        </div>
      )}
    </div>
  );
}
